'use client'

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/services/supabase'

interface User {
  id: string
  email: string
  username?: string
  full_name?: string
  avatar_url?: string
  created_at: string
}

export function useUser() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const getUser = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Get current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        console.error('Session error in useUser hook:', sessionError)
        throw new Error(sessionError.message)
      }

      if (!sessionData.session) {
        // No active session found in useUser hook
        setUser(null)
        return
      }

      // Get user profile data
      const { data: userData, error: userError } = await supabase.auth.getUser()

      if (userError) {
        console.error('User data error in useUser hook:', userError)
        throw new Error(userError.message)
      }

      if (userData.user) {
        // User data retrieved successfully

        // Get additional profile data if needed
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userData.user.id)
          .single()

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError)
        }

        setUser({
          id: userData.user.id,
          email: userData.user.email || '',
          username: profileData?.username,
          full_name: profileData?.full_name || userData.user.user_metadata?.full_name,
          avatar_url: profileData?.avatar_url || userData.user.user_metadata?.avatar_url,
          created_at: userData.user.created_at,
        })
      } else {
        // No user data found
        setUser(null)
      }
    } catch (err) {
      console.error('Error in useUser hook:', err)
      setError(err instanceof Error ? err.message : 'Failed to get user data')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Function to manually retry fetching user data
  const retry = useCallback(() => {
    setRetryCount(prev => prev + 1)
  }, [])

  useEffect(() => {
    // Initial fetch
    getUser()

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Auth state change in useUser hook

      if (session) {
        getUser()
      } else {
        setUser(null)
        setIsLoading(false)
      }
    })

    // Clean up subscription
    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [getUser, retryCount])

  return { user, isLoading, error, retry }
}
